import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../../components/atoms/SVG';
import { Namespace } from '../../../translation/namespace';
import { AppLibraryEditView } from '../views/AppLibraryEditView';
import { DefaultView, OptionalEditView, Views } from '../../../types/BlockWithViews';
import { AppLibraryDefaultView } from '../views/AppLibraryDefaultView';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { BlockBar, BlockBarAnchor, useBlockBarCreateHref } from '../../../_lib/components/BlockBar';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { useAppContext } from '../../../contexts/AppContext/AppContext';

export type AppLibraryBlockProps = Views<DefaultView<AppLibraryDefaultView> & OptionalEditView<AppLibraryEditView>>;

export function AppLibraryInteractions({ views, activeView }: AppLibraryBlockProps) {
  const { t } = useTranslation(Namespace.AppLibrary);
  const { editModeInfo } = useAppContext();
  const id = useHtmlValidId();
  const { refresh } = useRefreshBlock();
  const [currentActiveView, setCurrentActiveView] = useState(activeView);
  const currentActiveViewHref = useBlockBarCreateHref(
    currentActiveView,
    { default: views.edit?.href, edit: views.default?.href },
    id
  );

  const getActiveView = () => {
    if (currentActiveView === 'default') {
      return <AppLibraryDefaultView {...views.default} />;
    }

    if (currentActiveView === 'edit') {
      return <AppLibraryEditView {...(views.edit as AppLibraryEditView)} />;
    }

    return null;
  };

  return (
    <>
      {editModeInfo.active ? (
        <BlockBar heading={t('edit.titleText')} hierarchy={3}>
          <BlockBarAnchor
            href={currentActiveViewHref}
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
              event.stopPropagation();
              event.preventDefault();

              if (currentActiveView === 'default') {
                setCurrentActiveView('edit');
              } else if (currentActiveView === 'edit') {
                setCurrentActiveView('default');
                refresh();
              }
            }}
            text={t('edit.text')}
            svgSpriteId={currentActiveView === 'edit' ? Icons.DECLINE_CROSS : Icons.EDIT_PENCIL}
            aria-controls={id}
            aria-expanded={currentActiveView === 'edit'}
          />
        </BlockBar>
      ) : null}
      <div id={id}>{getActiveView()}</div>
    </>
  );
}
